/* eslint-disable filenames/match-regex, filenames/match-exported */

import AlertContextProvider from '@actinc/dls/context/AlertContext/provider';
import { ApolloProvider } from '@apollo/client';
import { EmotionCache } from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import * as Sentry from '@sentry/nextjs';
import { AppProps } from 'next/app';
import Head from 'next/head';
import React from 'react';
import { version } from 'package.json';
import CacheUserPhoto from '~/components/CacheUserPhoto';
import Debug from '~/components/Debug';
import ElectronListener from '~/components/ElectronListener';
import GuardDeviceId from '~/components/GuardDeviceId';
import RouteAnnouncer from '~/components/RouteAnnouncer';
// import RefreshToken from '~/components/RefreshToken';
import ENV from '~/constants/ENV';
import AppContextProvider from '~/context/AppContext/provider';
import DebugContextProvider from '~/context/DebugContext/provider';
import DeviceCheckContextProvider from '~/context/DeviceCheckContext/provider';
import createApolloClient from '~/helpers/createApolloClient';
import createEmotionCache from '~/helpers/createEmotionCache';
import theme from '~/styles/theme';
if (ENV.SENTRY_ENABLED === 'true') {
  Sentry.init({
    debug: true,
    dsn: ENV.SENTRY_DSN,
    environment: ENV.BUILD_ENV,
    release: version,
    // integrations: [Sentry.replayIntegration()],

    // Define how likely Replay events are sampled when an error occurs.
    // replaysOnErrorSampleRate: 1.0,

    // Define how likely Replay events are sampled.
    // This sets the sample rate to be 10%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    // replaysSessionSampleRate: 0.1,

    // Define how likely traces are sampled. Adjust this value in production, or use tracesSampler for greater control.
    tracesSampleRate: 1
  });
}
const clientSideEmotionCache = createEmotionCache();
interface IAppProps extends AppProps {
  emotionCache?: EmotionCache;
}
const App: React.FC<IAppProps> = ({
  Component,
  emotionCache = clientSideEmotionCache,
  pageProps
}: IAppProps): React.ReactElement => {
  const apolloClient = React.useMemo(() => {
    return createApolloClient();
  }, []);
  return <ApolloProvider client={apolloClient} data-sentry-element="ApolloProvider" data-sentry-component="App" data-sentry-source-file="_app.tsx">
      <AppContextProvider data-sentry-element="AppContextProvider" data-sentry-source-file="_app.tsx">
        <DebugContextProvider data-sentry-element="DebugContextProvider" data-sentry-source-file="_app.tsx">
          <DeviceCheckContextProvider data-sentry-element="DeviceCheckContextProvider" data-sentry-source-file="_app.tsx">
            <Head data-sentry-element="Head" data-sentry-source-file="_app.tsx">
              <meta content="width=device-width, initial-scale=1" name="viewport" data-sentry-element="meta" data-sentry-source-file="_app.tsx" />

              <link href="https://api.tiles.mapbox.com/mapbox-gl-js/v2.7.0/mapbox-gl.css" rel="stylesheet" />

              <link href="/static/img/favicons/apple-icon-57x57.png" rel="apple-touch-icon" sizes="57x57" />
              <link href="/static/img/favicons/apple-icon-60x60.png" rel="apple-touch-icon" sizes="60x60" />
              <link href="/static/img/favicons/apple-icon-72x72.png" rel="apple-touch-icon" sizes="72x72" />
              <link href="/static/img/favicons/apple-icon-76x76.png" rel="apple-touch-icon" sizes="76x76" />
              <link href="/static/img/favicons/apple-icon-114x114.png" rel="apple-touch-icon" sizes="114x114" />
              <link href="/static/img/favicons/apple-icon-120x120.png" rel="apple-touch-icon" sizes="120x120" />
              <link href="/static/img/favicons/apple-icon-144x144.png" rel="apple-touch-icon" sizes="144x144" />
              <link href="/static/img/favicons/apple-icon-152x152.png" rel="apple-touch-icon" sizes="152x152" />
              <link href="/static/img/favicons/apple-icon-180x180.png" rel="apple-touch-icon" sizes="180x180" />
              <link href="/static/img/favicons/apple-icon-192x192.png" rel="apple-touch-icon" sizes="192x192" type="image/png" />
              <link href="/static/img/favicons/favicon-32x32.png" rel="icon" sizes="32x32" type="image/png" />
              <link href="/static/img/favicons/favicon-96x96.png" rel="icon" sizes="96x96" type="image/png" />
              <link href="/static/img/favicons/favicon-16x16.png" rel="icon" sizes="16x16" type="image/png" />
              {/* <link href="/static/img/favicons/manifest.json" rel="manifest" /> */}

              <link href="/static/manifest.webmanifest.json" rel="manifest" />
              <meta content="/static/img/favicons/ms-icon-144x144.png" name="msapplication-TileImage" data-sentry-element="meta" data-sentry-source-file="_app.tsx" />
            </Head>

            <CacheProvider value={emotionCache} data-sentry-element="CacheProvider" data-sentry-source-file="_app.tsx">
              <RouteAnnouncer data-sentry-element="RouteAnnouncer" data-sentry-source-file="_app.tsx" />
              <ThemeProvider theme={theme} data-sentry-element="ThemeProvider" data-sentry-source-file="_app.tsx">
                <LocalizationProvider dateAdapter={AdapterDateFns} data-sentry-element="LocalizationProvider" data-sentry-source-file="_app.tsx">
                  <CssBaseline data-sentry-element="CssBaseline" data-sentry-source-file="_app.tsx" />
                  <AlertContextProvider data-sentry-element="AlertContextProvider" data-sentry-source-file="_app.tsx">
                    <GuardDeviceId data-sentry-element="GuardDeviceId" data-sentry-source-file="_app.tsx">
                      <ElectronListener data-sentry-element="ElectronListener" data-sentry-source-file="_app.tsx" />
                      <Component {...pageProps} data-sentry-element="Component" data-sentry-source-file="_app.tsx" />
                      <CacheUserPhoto data-sentry-element="CacheUserPhoto" data-sentry-source-file="_app.tsx" />
                      <Debug data-sentry-element="Debug" data-sentry-source-file="_app.tsx" />
                      {/* <RefreshToken /> */}
                    </GuardDeviceId>
                  </AlertContextProvider>
                </LocalizationProvider>
              </ThemeProvider>
            </CacheProvider>
          </DeviceCheckContextProvider>
        </DebugContextProvider>
      </AppContextProvider>
    </ApolloProvider>;
};
export default App;